
import Vue from "vue";
import { Component, Prop, Ref } from "vue-property-decorator";
import { configStore } from "@/store/store";
import defensiveCopy from "@/util/defensive-copy";
import { Preferences } from "@/types/preferences";
import * as data from "@/lib/wordpress/data";
import CustomBreakpointsPanel from "./Breakpoints/CustomBreakpointsPanel.vue";
import { Breakpoint } from "@/types/breakpoints";
import { CustomFontFamily } from "@/types/custom-fonts";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import { _Error, logErrorsToUiStore } from "@/lib/wordpress/data";
import CustomFontFamilies from "@/components/Settings/Preferences/CustomFonts/CustomFontFamilies.vue";
import Loading from "@/components/Reusable/Loading.vue";
import CustomUiSettings from "@/components/Settings/Preferences/CustomUiSettings.vue";
import Icon from "@/components/UI/Icon.vue";

@Component({
  components: {
    Icon,
    CustomUiSettings,
    CustomFontFamilies,
    CustomBreakpointsPanel,
    Loading
  }
})
export default class PreferencesModal extends Vue {
  @Prop()
  open!: boolean;

  @Ref() readonly customFontFamiliesPanel!: CustomFontFamilies;
  @Ref() readonly customBreakpointsPanel!: CustomBreakpointsPanel;

  tab = 0;
  currentTab = 0;

  uiSettingsTab = 0;
  fontFamiliesTab = 1;
  customBreakpointsTab = 2;

  get preferences() {
    return configStore.preferences;
  }

  get customBreakpoints() {
    return this.preferences.customBreakpoints;
  }

  get customFontFamilies() {
    return configStore.preferences.customFonts;
  }

  updateBreakpoints(newBreakpoints: Breakpoint[]) {
    const newPrefs = defensiveCopy(this.preferences);
    newPrefs.customBreakpoints = newBreakpoints;
    this.save(newPrefs);
  }

  updateCustomFontFamilies(newCustomFontFamilies: CustomFontFamily[]) {
    const newPrefs = defensiveCopy(this.preferences);
    newPrefs.customFonts = newCustomFontFamilies;
    this.save(newPrefs);
  }

  handleClickOutside() {
    this.checkForUnsavedChanges();
    if (!this.hasUnsavedChanges) {
      return this.$emit("update:dialog", false);
    }
    const exit = confirm("Exit without saving?");
    if (exit) {
      return this.$emit("update:dialog", false);
    }
    return this;
  }

  async handleChangeTab() {
    this.checkForUnsavedChanges();
    if (this.hasUnsavedChanges) {
      const exit = confirm("Exit without saving?");
      if (!exit) {
        await this.$nextTick();
        this.tab = this.currentTab;
        return true;
      }
    }
    this.currentTab = this.tab;
    return false;
  }

  saveInProgress = false;

  async save(newPrefs: Preferences) {
    this.saveInProgress = true;

    pipe(
      await data.saveToWP("preferences", newPrefs),
      fold(
        (errors: _Error[]) => {
          logErrorsToUiStore(errors);
        },
        () => {
          configStore.setPreferences(newPrefs);
        }
      )
    );
    this.saveInProgress = false;
    this.checkForUnsavedChanges();
  }

  hasUnsavedChanges = false;
  checkForUnsavedChanges() {
    if (this.currentTab === this.fontFamiliesTab) {
      this.hasUnsavedChanges =
        this.customFontFamiliesPanel.unsavedChangesPresent;
      return this.hasUnsavedChanges;
    }

    if (this.currentTab === this.customBreakpointsTab) {
      this.hasUnsavedChanges =
        this.customBreakpointsPanel.unsavedChangesPresent;
      return this.hasUnsavedChanges;
    }

    return (this.hasUnsavedChanges = false);
  }
}
